import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { normalizeFromEndpoint } from '../../../utils/endpointUtils';

const SMSConfig = ({
  config,
  onConfigChange,
  communicationEndpoints,
  onEndpointChange,
  onDelete,
}) => {

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    onConfigChange({
      ...config,
      [field]: value,
    });
  };

  const handleEndpointChange = (event) => {

    const selectedEndpoint = communicationEndpoints.find(ep => ep.id === event.target.value);

    if (selectedEndpoint) {
      onEndpointChange(selectedEndpoint.id);
      onConfigChange({
        ...config,
        from_endpoint: selectedEndpoint.id,
        from_endpoint_value: selectedEndpoint.value
      });
    }
  };

  const handleMediaDelete = (index) => {
    const newMediaUrls = [...config.media_urls];
    newMediaUrls.splice(index, 1);
    onConfigChange({
      ...config,
      media_urls: newMediaUrls,
    });
  };

  // Filter endpoints to only show SMS endpoints
  const smsEndpoints = communicationEndpoints.filter(endpoint => 
    endpoint.channel_list && endpoint.channel_list.includes('sms')
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        SMS Configuration
      </Typography>

      <FormControl fullWidth margin="normal" required error={!config.from_endpoint}>
        <InputLabel>From Number</InputLabel>
        <Select
          value={config.from_endpoint || ''}
          onChange={handleEndpointChange}
          label="From Number"
        >
          {smsEndpoints.map(endpoint => (
            <MenuItem key={endpoint.id} value={endpoint.id}>
              {endpoint.value || config.from_endpoint_value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel>Priority</InputLabel>
        <Select
          value={config.priority || 'normal'}
          onChange={handleConfigChange('priority')}
          label="Priority"
        >
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="normal">Normal</MenuItem>
          <MenuItem value="low">Low</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={config.track_delivery ?? true}
              onChange={handleConfigChange('track_delivery')}
            />
          }
          label="Track Delivery"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={config.track_replies ?? true}
              onChange={handleConfigChange('track_replies')}
            />
          }
          label="Track Replies"
        />
      </Box>

      {config.media_urls && config.media_urls.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Media URLs
          </Typography>
          {config.media_urls.map((url, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ flex: 1 }}>{url}</Typography>
              <IconButton
                size="small"
                onClick={() => handleMediaDelete(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {onDelete && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          sx={{ mt: 2 }}
        >
          Remove SMS Configuration
        </Button>
      )}
    </Box>
  );
};

SMSConfig.propTypes = {
  config: PropTypes.shape({
    from_endpoint: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    from_endpoint_value: PropTypes.string,
    priority: PropTypes.oneOf(['high', 'normal', 'low']),
    track_delivery: PropTypes.bool,
    track_replies: PropTypes.bool,
    media_urls: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      channel_list: PropTypes.arrayOf(PropTypes.string).isRequired,
      platform: PropTypes.string,
      priority: PropTypes.string,
      is_primary: PropTypes.bool,
      is_verified: PropTypes.bool,
    })
  ).isRequired,
  onConfigChange: PropTypes.func.isRequired,
  onEndpointChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default SMSConfig; 